// Wires the Inputs Rail "+ Add input" affordance to a real picker.
//
// Today the backend already creates webhook + email + file_upload +
// api_pull + manual inputs on every Source create
// (services/app/src/routes/sources.ts:buildInputs); so the dialog isn't
// "add a new input row" — it's "discover and route to the existing
// surface for the kind you want." Each card deep-links to where the
// operator actually configures that channel.
//
// Per-row controls: small "rename" + "pause/resume" ghost buttons sit
// in the card header. Clicking them is e.stopPropagation'd so the
// card's onClick (open the channel-specific surface) doesn't fire.
//
// Extracted from pages/SourceDetailPage.tsx as part of the file split
// (Apr 2026).

import React, { useState } from 'react';
import type { Source, SourceInput } from '@datahub/shared';
import { patchSourceInput } from '../../api';
import { useToast } from '../Toast';

export function AddInputDialog({
  source, onClose, onOpenEmail, onOpenForm, onOpenUpload, onOpenSharePanel, onOpenSendCustom, onOpenSchedules,
}: {
  source: Source;
  onClose: () => void;
  onOpenEmail: () => void;
  onOpenForm: () => void;
  /** Opens the operator file-picker (UploadDialog). */
  onOpenUpload: () => void;
  /** Opens the partner-facing share panel (RawUploadPanel) — URL +
   *  drag-drop demo + accepted formats. Optional; when omitted, the
   *  Public upload URL card falls back to onOpenUpload. */
  onOpenSharePanel?: () => void;
  onOpenSendCustom: () => void;
  onOpenSchedules: () => void;
}) {
  const toast = useToast();
  // Local mirror of source.inputs so per-row pause/rename reflects
  // immediately without remounting the parent. Backend stays the
  // source-of-truth on next refetch.
  const [inputs, setInputs] = useState<SourceInput[]>(() => source.inputs);
  const [editing, setEditing] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  const inputByKind = (kind: string) => inputs.find(i => i.kind === kind);

  function applyUpdate(updated: SourceInput): void {
    setInputs(prev => prev.map(i => i.input_id === updated.input_id ? updated : i));
    const idx = source.inputs.findIndex(i => i.input_id === updated.input_id);
    if (idx >= 0) source.inputs[idx] = updated;
  }

  async function flipStatus(input: SourceInput): Promise<void> {
    const next: 'active' | 'paused' = input.status === 'paused' ? 'active' : 'paused';
    setBusy(input.input_id);
    try {
      const r = await patchSourceInput(source.source_id, input.input_id, { status: next });
      applyUpdate(r);
      toast.show(next === 'paused' ? `Paused "${input.name?.trim() || input.kind}".` : `Resumed "${input.name?.trim() || input.kind}".`, { kind: 'success' });
    } catch (e) {
      toast.show(`Status flip failed · ${(e as Error).message}`, { kind: 'error' });
    } finally {
      setBusy(null);
    }
  }

  async function commitRename(input: SourceInput): Promise<void> {
    const trimmed = draftName.trim();
    setEditing(null);
    if (trimmed === (input.name ?? '').trim()) return; // no-op
    setBusy(input.input_id);
    try {
      const r = await patchSourceInput(source.source_id, input.input_id, { name: trimmed || null });
      applyUpdate(r);
      toast.show(trimmed ? `Renamed to "${trimmed}".` : 'Custom name cleared.', { kind: 'success' });
    } catch (e) {
      toast.show(`Rename failed · ${(e as Error).message}`, { kind: 'error' });
    } finally {
      setBusy(null);
    }
  }
  const kinds: Array<{ kind: 'email' | 'file_upload' | 'file_upload_test' | 'api_pull' | 'manual'; name: string; blurb: string; cta: string; onClick: () => void; status: 'active' | 'paused' | 'unknown' }> = [
    {
      kind: 'api_pull',
      name: 'REST API pull',
      blurb: 'Fetch a JSON URL on a schedule. Configure cron + headers, see retry policy. Existing schedules live in the Schedules tab.',
      cta: 'Open Schedules tab →',
      onClick: () => { onOpenSchedules(); onClose(); },
      status: (inputByKind('api_pull')?.status as 'active' | 'paused') ?? 'unknown',
    },
    {
      kind: 'file_upload',
      name: 'Public upload form',
      blurb: source.form_config
        ? `Hosted form is enabled at /form/${source.slug_alias ?? source.source_id}. ${source.form_config.fields.length} field${source.form_config.fields.length === 1 ? '' : 's'} defined.`
        : 'Hand a partner a URL — they fill the form and submit. Custom field schema (text/num/email/select/file) with prefilled-vs-locked semantics. Each submit becomes one event.',
      cta: source.form_config ? 'Edit form schema →' : 'Configure public form →',
      onClick: () => { onOpenForm(); onClose(); },
      status: (source.form_config?.enabled ? 'active' : 'paused') as 'active' | 'paused',
    },
    {
      kind: 'file_upload_test',
      name: 'Public upload URL (share + test)',
      blurb: 'Hand a partner the per-source /upload URL. Opens a panel with the URL + copy button + drag-and-drop demo + accepted formats list. Same pipeline as the operator upload dialog — just remote-friendly.',
      cta: 'Open share panel →',
      onClick: () => { (onOpenSharePanel ?? onOpenUpload)(); onClose(); },
      status: 'active' as const,
    },
    {
      kind: 'email',
      name: 'Email parse',
      blurb: `Send mail to ${inputByKind('email')?.address ?? 'an inbound address'} and the body becomes events. (Email ingestion handler is on the roadmap; the address is reserved.)`,
      cta: 'Copy inbound address →',
      onClick: () => { onOpenEmail(); onClose(); },
      status: (inputByKind('email')?.status as 'active' | 'paused') ?? 'unknown',
    },
    {
      kind: 'manual',
      name: 'Manual insert',
      blurb: 'Send a one-off custom JSON payload from the dashboard. Useful for testing trigger configs without involving the partner.',
      cta: 'Send a custom event →',
      onClick: () => { onOpenSendCustom(); onClose(); },
      status: (inputByKind('manual')?.status as 'active' | 'paused') ?? 'unknown',
    },
  ];

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 50, background: 'oklch(0.18 0.01 250 / 0.4)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: 80 }}>
      <div onClick={e => e.stopPropagation()} style={{ width: 'min(640px, calc(100vw - 48px))', background: 'var(--bg-elev)', border: '1px solid var(--line)', borderRadius: 12, padding: 24, maxHeight: 'calc(100vh - 120px)', overflow: 'auto' }}>
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>Add an input channel</div>
        <div style={{ fontSize: 12.5, color: 'var(--ink-2)', marginBottom: 16, lineHeight: 1.5 }}>
          Every Source already accepts all 5 channels. Pick one to configure or test.
          The active webhook is shown in the rail above; the others live behind these CTAs.
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {kinds.map(k => {
            // The 'file_upload_test' card mirrors the file_upload input row so
            // per-row controls operate on the underlying input. All other
            // cards key directly off their kind.
            const inputForRow = inputByKind(k.kind === 'file_upload_test' ? 'file_upload' : k.kind);
            const editable = !!inputForRow;
            const isEditing = editable && editing === inputForRow!.input_id;
            const isBusy = editable && busy === inputForRow!.input_id;
            const displayName = inputForRow?.name?.trim() || k.name;
            return (
              <div
                key={k.kind}
                onClick={isEditing ? undefined : k.onClick}
                style={{
                  textAlign: 'left',
                  padding: 12,
                  background: 'var(--bg-sunken)',
                  border: '1px solid var(--line)',
                  borderRadius: 8,
                  cursor: isEditing ? 'default' : 'pointer',
                  color: 'var(--ink)',
                  display: 'flex', flexDirection: 'column', gap: 4,
                  opacity: isBusy ? 0.6 : 1,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                  {isEditing ? (
                    <input
                      autoFocus
                      value={draftName}
                      maxLength={60}
                      onClick={e => e.stopPropagation()}
                      onChange={e => setDraftName(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') { e.preventDefault(); void commitRename(inputForRow!); }
                        else if (e.key === 'Escape') { e.preventDefault(); setEditing(null); }
                      }}
                      onBlur={() => { void commitRename(inputForRow!); }}
                      placeholder={k.name}
                      style={{
                        fontSize: 13, fontWeight: 600, padding: '1px 5px',
                        border: '1px solid var(--line-strong, var(--line))',
                        borderRadius: 4, outline: 'none', minWidth: 160,
                        background: 'var(--bg)', color: 'inherit', font: 'inherit',
                      }}
                    />
                  ) : (
                    <strong style={{ fontSize: 13 }}>{displayName}</strong>
                  )}
                  <span style={{
                    fontFamily: 'var(--mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em',
                    padding: '2px 6px', borderRadius: 3,
                    color: k.status === 'active' ? 'oklch(0.42 0.12 150)' : 'var(--ink-3)',
                    background: k.status === 'active' ? 'oklch(0.92 0.06 150)' : 'var(--bg)',
                    border: `1px solid ${k.status === 'active' ? 'oklch(0.78 0.10 150)' : 'var(--line)'}`,
                  }}>{k.status}</span>
                  {editable && !isEditing && (
                    <>
                      <button
                        type="button"
                        onClick={e => { e.stopPropagation(); setDraftName(inputForRow!.name ?? ''); setEditing(inputForRow!.input_id); }}
                        title="Rename this input"
                        disabled={isBusy}
                        style={{ background: 'transparent', border: 'none', padding: '2px 4px', fontSize: 10.5, fontFamily: 'var(--mono)', color: 'var(--ink-3)', cursor: 'pointer' }}
                      >rename</button>
                      <button
                        type="button"
                        onClick={e => { e.stopPropagation(); void flipStatus(inputForRow!); }}
                        title={k.status === 'paused' ? 'Resume — accept events again' : 'Pause — return 503 Retry-After'}
                        disabled={isBusy}
                        style={{ background: 'transparent', border: 'none', padding: '2px 4px', fontSize: 10.5, fontFamily: 'var(--mono)', color: 'var(--ink-3)', cursor: 'pointer' }}
                      >{k.status === 'paused' ? 'resume' : 'pause'}</button>
                    </>
                  )}
                  <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--accent)', fontFamily: 'var(--mono)' }}>{k.cta}</span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--ink-2)', lineHeight: 1.45 }}>{k.blurb}</div>
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <button className="btn ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
